import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getFarms,
  getOnboardingFarm,
  getOnboardingFields,
} from "regen-api-ts-client";
import { registerWithoutFarmMutation } from "regen-api-ts-client/react-query";

import { LocalStorageService } from "./services/LocalStorage";
import { useRegenQuery } from "./util/apiHooks";
import { ONBOARDING_ROUTES } from "./util/constants";
import { useStoreFeatureFlags } from "./util/featureFlags";
import { useHasSomeRoles } from "./util/hooks";

const dashboardEntryPaths = ["/", "/auth/callback"];
const onboardingEntryPathRegex = RegExp("^/((auth|onboarding)/?.*){0,1}$");

function isOnboardingEnabled(isUserFetched: boolean, isAdminOrStaff: boolean) {
  // if url isn't `/` or `/onboarding*` or `/auth/callback`, then don't run
  // these calls - as the user has moved beyond the onboarding process
  const isOnboardingRoute = onboardingEntryPathRegex.test(
    window.location.pathname,
  );
  // if user is fetched, then only run these calls if not an admin and
  // we might need to load onboarding or redirect the user (which requires
  // onboarding information)
  return isUserFetched && !isAdminOrStaff && isOnboardingRoute;
}

function useGetGrowerRouting(
  isUserFetched: boolean,
  isUserVerified: boolean | undefined,
  isAdminOrStaff: boolean,
) {
  const onboardingEnabled = isOnboardingEnabled(isUserFetched, isAdminOrStaff);
  const { data: farmList, isFetched: farmFetched } = useRegenQuery(getFarms, {
    throwOnError: false,
  });
  const { data: onboardingFields, isFetched: onboardingFieldsFetched } =
    useRegenQuery(getOnboardingFields, {
      throwOnError: false,
      enabled: onboardingEnabled,
    });
  const { data: onboardingFarm, isFetched: onboardingFarmFetched } =
    useRegenQuery(getOnboardingFarm, {
      throwOnError: false,
      enabled: onboardingEnabled,
    });

  let growerRoute: string | undefined = undefined;
  // Check that all the API calls have returned (possibly with 401 or 404
  // result!) before deciding what route to direct the user to.
  const canRoute =
    isUserFetched &&
    farmFetched &&
    onboardingFieldsFetched &&
    onboardingFarmFetched;
  if (canRoute) {
    if (farmFetched && farmList && farmList.length > 0) {
      growerRoute = `/farm/${farmList[0].id}`;
    } else if (!onboardingFarm || !onboardingFarm?.label) {
      growerRoute = `/onboarding/${ONBOARDING_ROUTES.WELCOME}`;
    } else if (!onboardingFields || onboardingFields.length === 0) {
      growerRoute = `/onboarding/${ONBOARDING_ROUTES.FIELD_BOUNDARIES}`;
    } else if (!onboardingFarm?.yearsOperated != null) {
      growerRoute = `/onboarding/${ONBOARDING_ROUTES.FARM_DETAILS}`;
    } else if (!isUserVerified) {
      growerRoute = `/onboarding/${ONBOARDING_ROUTES.VERIFY_EMAIL}`;
    } else {
      growerRoute = `/onboarding/${ONBOARDING_ROUTES.WELCOME}`;
    }
  }
  return {
    growerRoute,
  };
}

function App() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { farmId } = useParams();

  const registerWithoutFarm = useMutation(registerWithoutFarmMutation());
  const {
    data: isAdminOrStaff,
    me: user,
    isFetched: isUserFetched,
  } = useHasSomeRoles(["admin", "staff"]);
  const { growerRoute } = useGetGrowerRouting(
    isUserFetched,
    user?.emailVerified,
    isAdminOrStaff,
  );
  // If user has added any feature flags to the url, add them to local storage
  useStoreFeatureFlags();

  // Set the user's role in sentry
  useEffect(() => {
    if (isUserFetched && user) {
      Sentry.setUser({
        id: user.id || undefined,
        email: user.email,
        username: user.displayName,
      });
      Sentry.setTag("vayda.isAdminOrStaff", isAdminOrStaff);
    }
  }, [user, isUserFetched, isAdminOrStaff]);

  // Capture currently accessed farm id, if farmId is in the url params
  useEffect(() => {
    if (farmId) {
      Sentry.setTag("vayda.farmId", farmId);
    }
  }, [farmId]);

  // Set local storage item upon successful login (if this component has loaded,
  // the user has authenticated thanks to AuthenticationGuard).
  useEffect(() => {
    LocalStorageService.setItem(
      LocalStorageService.Keys.HAS_AUTHENTICATED_ONCE,
      "true",
    );
  }, []);

  useEffect(() => {
    // If the user is an admin/staff and does not have an id (first time
    // logging in), then register the user. Note that non admin/staff will be
    // registered along with their farm in the onboarding flow.
    if (
      isAdminOrStaff &&
      !user?.id &&
      !(registerWithoutFarm.isPending || registerWithoutFarm.isSuccess)
    ) {
      registerWithoutFarm.mutate(
        {},
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["my_user"] });
            queryClient.invalidateQueries({ queryKey: ["all_farms"] });
          },
        },
      );
    }
  }, [user, isAdminOrStaff, registerWithoutFarm, queryClient]);

  // Redirect the user appropriately based on their role or existence of farm
  useEffect(() => {
    if (!user || !dashboardEntryPaths.includes(pathname)) {
      // Can't determine if user is admin or staff yet, so don't prematurely
      // redirect them to onboarding.
      // Don't redirect if the user isn't at one of the entry paths.
      return;
    }

    if (isAdminOrStaff) {
      navigate("/admin");
    } else if (growerRoute) {
      // if there's a grower route either they are onboarding or they have farms
      navigate(growerRoute);
    }
    // no route to go to yet! this may mean there is a problem?
  }, [user, navigate, isAdminOrStaff, growerRoute, pathname]);

  return <Outlet />;
}

export default App;
